// @flow

import * as React from "react";
import cx from "classnames";

import Tile from "../../Tile/GridTile";

import styles from "./Grid.scss";
import TileListLogic from "../shared/TileListLogic";

import type { InputType } from "../../Input/TextInput";
import type { TextType } from "../../TooltipContent";
import type { TileClassNames } from "../../Tile/GenericTile/GenericTile";
import type { SelectionMode } from "../shared/TileListLogic";

type GridProps = {
  columns: number,
  input: InputType,
  selectionMode: SelectionMode,
  tiles: Array<?{
    src: string,
    alt: string,
    specificStyles?: TileClassNames,
    value: any,
    title: string,
    tooltipContent: Array<TextType>
  }>,
  horizontalGap: string,
  verticalGap: string,
  generalStyles: TileClassNames,
  tileMaxWidth: string
};

type GridState = {
  columns: number
};

const widthFor1Column = 500;
const widthFor2Columns = 700;

class Grid extends React.Component<GridProps, GridState> {
  static defaultProps = {
    selectionMode: "single",
    tiles: [],
    horizontalGap: "1rem",
    verticalGap: "1rem",
    generalStyles: {
      button: "",
      buttonActive: "",
      svgWrapper: ""
    },
    tileMaxWidth: "none"
  };

  state = {
    columns: 0
  };

  componentDidMount() {
    this.calculateColumns();

    window.addEventListener("resize", this.calculateColumns);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.calculateColumns);
  }

  calculateColumns = () => this.setState({ columns: this.getColumnsForWidth() });

  getColumnsForWidth = () => {
    const { columns: columnsFromProps } = this.props;

    const width = window.innerWidth;

    let newColumns = 0;

    if (width < widthFor1Column) {
      newColumns = 1;
    } else if (width < widthFor2Columns) {
      newColumns = 2;
    } else {
      newColumns = columnsFromProps;
    }

    return newColumns;
  };

  getListContainerStyles = () => {
    const { verticalGap } = this.props;

    return {
      marginTop: `-${verticalGap}`
    };
  };

  getTileStyles = (tileIndex: number) => {
    const { tiles, horizontalGap, verticalGap, tileMaxWidth } = this.props;
    const { columns } = this.state;

    return {
      width: `calc((100% / ${columns}) - (${horizontalGap} * ${
        columns - 1
      } / ${columns}) - 0.01px)`,
      maxWidth: window.innerWidth <= widthFor2Columns ? "100%" : tileMaxWidth,
      marginRight:
        (tileIndex + 1) % columns && tileIndex + 1 !== tiles.length ? `${horizontalGap}` : 0,
      marginTop: `${verticalGap}`
    };
  };

  renderTiles = (
    isActive: (currentValue: any) => boolean,
    handleClick: (selectedValue: any) => void
  ) => {
    const {
      tiles,
      generalStyles,
      selectionMode,
      input: { name }
    } = this.props;

    return tiles.map<React.Node>((tile, tileIndex) => {
      const { title, src, alt, specificStyles, value, tooltipContent } = tile || {};

      return (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={tileIndex}
          style={this.getTileStyles(tileIndex)}
        >
          {tile ? (
            <Tile
              name={name}
              title={title}
              id={`${name}-${value}`}
              src={src}
              alt={alt}
              generalStyles={generalStyles}
              specificStyles={specificStyles}
              isActive={isActive(value)}
              onClick={() => handleClick(value)}
              selectionMode={selectionMode}
              tooltipContent={tooltipContent}
            />
          ) : null}
        </div>
      );
    });
  };

  render() {
    const { input, selectionMode } = this.props;

    return (
      <TileListLogic input={input} selectionMode={selectionMode}>
        {({ isActive, handleClick }) => (
          <div
            className={cx(styles.listContainer)}
            style={this.getListContainerStyles()}
            role={selectionMode === "single" ? "radiogroup" : undefined}
          >
            {this.renderTiles(isActive, handleClick)}
          </div>
        )}
      </TileListLogic>
    );
  }
}

export default Grid;
